import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import classes from '../../../pipeline.module.scss';
import Api from '../../../../../../../api/api';
import Table from '../../../../../../_common/Tables/Table';
import TableAndPagination from '../../../../../../_common/TableAndPagination/TableAndPagination';
import Modal from '../../../../../../_common/Modal/Modal';
import Loader from '../../../../../../_common/Loader/Loader';
import StatusField from '../../../../../../_common/StatusField/StatusField';
import { Card } from '../../PipelineComponents';
import { formattedDate } from '../../../pipelineHelpers';

const title = 'Members on a short supply who could be on 90/100';

const tableHeader = [
  { name: 'Member', path: 'name' },
  { name: 'Medication', path: 'productName' },
  { name: 'Days supply', path: 'daysSupply' },
  { name: 'Filled', path: 'billOnDate' },
  { name: 'Refills left', path: 'refillsRemaining' },
  {
    name: 'Status',
    path: 'status',
    render: (d) => (d.status ? <StatusField status={d.status} /> : '-'),
  },
];

const formattedMembers = (list) => (list || []).map((d) => ({
  ...d,
  id: d.scriptId,
  name: [d.firstName, d.lastName].filter(Boolean).join(' '),
  daysSupply: d.daysSupply?.toString(),
  billOnDate: formattedDate(d.billOnDate),
}));

const modalInitialState = {
  open: false,
  firstLoad: true,
  loading: false,
  list: [],
  totalPage: 1,
  page: 1,
  limit: 15,
};

const ShortSupplyMembersCard = ({ params }) => {
  const navigate = useNavigate();

  const firstLoad = useRef(true);

  const [membersData, setMembersData] = useState([]);
  const [showSeeMore, showSeeMoreToggle] = useState(false);
  const [loading, loadingToggle] = useState(true);

  const [modal, setModal] = useState(modalInitialState);

  useEffect(() => {
    (async () => {
      try {
        loadingToggle(true);

        const { data } = await Api.getShortSupply({ ...params, limit: 10, page: 1 });

        setMembersData(formattedMembers(data?.data?.response));

        showSeeMoreToggle(data?.data?._meta?.pageCount > 1);

        loadingToggle(false);
        firstLoad.current = false;
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [params]);

  useEffect(() => {
    if (!modal.open) return;

    (async () => {
      try {
        setModal((prev) => ({ ...prev, loading: true }));

        const { data } = await Api.getShortSupply({
          ...params, page: modal.page, limit: modal.limit,
        });

        setModal((prev) => ({
          ...prev,
          list: formattedMembers(data?.data?.response),
          totalPage: data?.data?._meta?.pageCount || 1,
        }));

        setModal((prev) => ({ ...prev, loading: false, firstLoad: false }));
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [params, modal.open, modal.page, modal.limit]);

  const openModal = () => setModal({ ...modalInitialState, open: true });

  const onChangePageData = (path, value) => {
    setModal((prev) => (path === 'limit'
      ? { ...prev, limit: value, page: 1 }
      : { ...prev, page: value }));
  };

  return (
    <>
      <Card
        title={title}
        caption="Every fill under a 90-day supply in the period"
        loading={loading}
        firstLoad={firstLoad.current}
        isEmpty={!membersData.length}
      >
        <div className={classes.pipeline_table}>
          <Table
            header={tableHeader}
            data={membersData}
            className={classes.clickable_row}
            onClick={(scriptId) => navigate(`/scripts/${scriptId}`)}
          />
        </div>

        {showSeeMore && (
          <div className={classes.see_more_wrapper}>
            <button
              type="button"
              className={classes.see_more_btn}
              onClick={openModal}
            >
              See more
            </button>
          </div>
        )}
      </Card>

      <Modal
        open={modal.open}
        onClose={() => setModal((prev) => ({ ...prev, open: false }))}
        className={classes.members_modal}
      >
        <div className={classes.members_modal_content}>
          <h2>{title}</h2>

          <div className={classes.members_modal_table}>
            {modal.firstLoad
              ? (
                <div className={classes.members_modal_loader}>
                  <Loader />
                </div>
              )

              : (
                <TableAndPagination
                  header={tableHeader}
                  data={modal.list}
                  loading={modal.loading}
                  onChange={onChangePageData}
                  className={classes.clickable_row}
                  onClick={(scriptId) => navigate(`/scripts/${scriptId}`)}
                  totalPage={modal.totalPage}
                  page={modal.page - 1}
                  limit={modal.limit}
                />
              )}
          </div>
        </div>
      </Modal>
    </>
  );
};

ShortSupplyMembersCard.propTypes = {
  params: PropTypes.object.isRequired,
};

export default ShortSupplyMembersCard;
